import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { isLondonPostcode, outwardPostcode } from "@/lib/postcode";

// ---- Static fallback (matches helper-onboarding RATE_DEFAULTS) -------------
const RATE_DEFAULTS: Record<string, { low: number; mid: number; high: number }> = {
  handyman: { low: 30, mid: 45, high: 60 },
  "furniture-assembly": { low: 25, mid: 35, high: 50 },
  "tv-mounting": { low: 35, mid: 50, high: 70 },
  moving: { low: 25, mid: 40, high: 55 },
  "furniture-removal": { low: 30, mid: 45, high: 65 },
  "heavy-lifting": { low: 25, mid: 40, high: 60 },
  "ikea-assembly": { low: 25, mid: 35, high: 50 },
  "furniture-disassembly": { low: 25, mid: 35, high: 50 },
  "shelf-mounting": { low: 30, mid: 45, high: 60 },
  "picture-hanging": { low: 25, mid: 40, high: 55 },
  "curtain-blind-installation": { low: 30, mid: 45, high: 60 },
  "home-repairs": { low: 30, mid: 50, high: 65 },
  "appliance-installation": { low: 35, mid: 50, high: 70 },
  "door-repair": { low: 35, mid: 50, high: 70 },
  "drywall-repair": { low: 35, mid: 50, high: 70 },
};
const DEFAULT_DURATION: Record<string, number> = {
  handyman: 90,
  "furniture-assembly": 90,
  "tv-mounting": 75,
  moving: 120,
  "furniture-removal": 90,
  "heavy-lifting": 90,
  "ikea-assembly": 90,
  "furniture-disassembly": 75,
  "shelf-mounting": 60,
  "picture-hanging": 60,
  "curtain-blind-installation": 75,
  "home-repairs": 120,
  "appliance-installation": 90,
  "door-repair": 90,
  "drywall-repair": 120,
};

// Inner London prefixes get a small uplift; outer London neutral.
const INNER_LONDON_OUTWARD = /^(EC|WC|E1|E2|E14|N1|N5|NW1|NW3|NW5|NW8|SE1|SE11|SE16|SW1|SW3|SW5|SW6|SW7|SW10|W1|W2|W8|W11|W14)$/i;

const ALLOWED = [
  "handyman", "tv-mounting", "furniture-assembly", "moving",
  "furniture-removal", "heavy-lifting", "ikea-assembly", "furniture-disassembly",
  "shelf-mounting", "picture-hanging", "curtain-blind-installation",
  "home-repairs", "appliance-installation", "door-repair", "drywall-repair",
] as const;
type Allowed = (typeof ALLOWED)[number];

const InputSchema = z.object({
  category_slug: z.enum(ALLOWED),
  postcode: z.string().trim().min(2).max(10),
  estimated_duration_minutes: z.number().int().min(15).max(8 * 60).optional(),
  complexity: z.enum(["low", "normal", "high"]).optional(),
  urgency: z.enum(["low", "normal", "high"]).optional(),
});

export type PriceEstimate = {
  currency: "GBP";
  low: number;
  expected: number;
  premium: number;
  confidence: number; // 0..1
  confidence_label: "low" | "medium" | "high";
  hourly_rate: { low: number; expected: number; premium: number };
  duration_minutes: number;
  basis: {
    category_slug: Allowed;
    sample_size: number;
    data_source: "historical_bookings" | "active_helpers" | "category_baseline";
    location_tier: "inner_london" | "outer_london" | "unknown";
    complexity: "low" | "normal" | "high";
    historical_window_days: number;
  };
  disclaimer: string;
};

function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return 0;
  const idx = (sorted.length - 1) * q;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}
function round5(n: number): number {
  return Math.round(n / 5) * 5;
}

export const estimateJobPrice = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => InputSchema.parse(d))
  .handler(async ({ data }): Promise<PriceEstimate> => {
    const url = process.env.SUPABASE_URL;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!url || !key) throw new Error("Pricing service unavailable");

    // Anon Data API client — reads helper_profiles/categories under public SELECT policies
    const supabase = createClient<Database>(url, key, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });

    const outward = outwardPostcode(data.postcode).toUpperCase();
    const locationTier: "inner_london" | "outer_london" | "unknown" =
      !outward ? "unknown"
        : INNER_LONDON_OUTWARD.test(outward) ? "inner_london"
        : isLondonPostcode(data.postcode) ? "outer_london" : "unknown";
    const locationMult = locationTier === "inner_london" ? 1.10 : 1.0;

    const complexity: "low" | "normal" | "high" =
      data.complexity ?? (data.urgency === "high" ? "high" : "normal");
    const complexityMult = complexity === "low" ? 0.85 : complexity === "high" ? 1.2 : 1.0;

    const baseline = RATE_DEFAULTS[data.category_slug];
    const duration = data.estimated_duration_minutes ?? DEFAULT_DURATION[data.category_slug];

    // --- 1) Try historical bookings (last 180 days, completed, GBP) ---------
    let sampleSize = 0;
    let dataSource: PriceEstimate["basis"]["data_source"] = "category_baseline";
    let rateLow = baseline.low;
    let rateMid = baseline.mid;
    let rateHigh = baseline.high;
    const windowDays = 180;

    try {
      const since = new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000).toISOString();
      const { data: rows } = await supabase
        .from("bookings")
        .select("agreed_rate, jobs!inner(category_id, categories!inner(slug))")
        .eq("status", "completed")
        .eq("currency", "GBP")
        .eq("jobs.categories.slug", data.category_slug)
        .gte("created_at", since)
        .limit(500);
      const rates = (rows ?? [])
        .map((r: { agreed_rate: number | null }) => Number(r.agreed_rate))
        .filter((n) => Number.isFinite(n) && n >= 10 && n <= 200)
        .sort((a, b) => a - b);
      if (rates.length >= 8) {
        rateLow = quantile(rates, 0.25);
        rateMid = quantile(rates, 0.5);
        rateHigh = quantile(rates, 0.85);
        sampleSize = rates.length;
        dataSource = "historical_bookings";
      }
    } catch {
      // fall through
    }

    // --- 2) Fall back to active helper rates --------------------------------
    if (dataSource === "category_baseline") {
      try {
        // categories.slug -> id
        const { data: cat } = await supabase
          .from("categories")
          .select("id")
          .eq("slug", data.category_slug)
          .maybeSingle();
        if (cat?.id) {
          const { data: skillRows } = await supabase
            .from("helper_skills")
            .select("hourly_rate")
            .eq("category_id", cat.id)
            .not("hourly_rate", "is", null)
            .limit(500);
          const rates = (skillRows ?? [])
            .map((r) => Number(r.hourly_rate))
            .filter((n) => Number.isFinite(n) && n >= 10 && n <= 200)
            .sort((a, b) => a - b);
          if (rates.length >= 5) {
            rateLow = quantile(rates, 0.25);
            rateMid = quantile(rates, 0.5);
            rateHigh = quantile(rates, 0.85);
            sampleSize = rates.length;
            dataSource = "active_helpers";
          }
        }
      } catch {
        // ignore — keep baseline
      }
    }

    // Apply multipliers
    rateLow *= locationMult * (complexity === "low" ? 0.9 : 1);
    rateMid *= locationMult * complexityMult;
    rateHigh *= locationMult * (complexity === "high" ? 1.15 : 1.05);

    const hours = duration / 60;
    const low = round5(rateLow * hours);
    const expected = round5(rateMid * hours);
    const premium = round5(rateHigh * hours);

    // --- Confidence ---------------------------------------------------------
    let confidence = 0.4; // baseline only
    if (dataSource === "active_helpers") confidence = 0.55 + Math.min(sampleSize / 40, 1) * 0.2;
    if (dataSource === "historical_bookings") confidence = 0.7 + Math.min(sampleSize / 60, 1) * 0.25;
    if (locationTier === "unknown") confidence *= 0.8;
    if (complexity === "high") confidence *= 0.9;
    confidence = Math.max(0.25, Math.min(0.95, Number(confidence.toFixed(2))));
    const confidence_label = confidence >= 0.75 ? "high" : confidence >= 0.5 ? "medium" : "low";

    return {
      currency: "GBP",
      low,
      expected,
      premium,
      confidence,
      confidence_label,
      hourly_rate: {
        low: Math.round(rateLow),
        expected: Math.round(rateMid),
        premium: Math.round(rateHigh),
      },
      duration_minutes: duration,
      basis: {
        category_slug: data.category_slug,
        sample_size: sampleSize,
        data_source: dataSource,
        location_tier: locationTier,
        complexity,
        historical_window_days: windowDays,
      },
      disclaimer:
        "Estimate only — not a guaranteed quote. The final price is set by the Helper when they accept your job, based on the work involved. You only pay once it's done.",
    };
  });
